import { createAsyncThunk } from '@reduxjs/toolkit';

import { client } from 'src/graphql/client';
import { MARK_CUSTOMER_PICKUP_STOPS_COMPLETE } from 'src/graphql/requests/markCustomerPickupStopsComplete';
import { GMarkCustomerPickupStopsCompleteMutation } from 'src/graphql/types/generated';
import analytics from 'src/services/analytics';
import { logWarning } from 'src/services/datadog';
import {
  isSelectedNightEndBeforeWarehouseNightEnd,
  selectedNightDaysPastSelector,
  selectedWarehouseSelector,
} from 'src/state/selectors/delivery';
import type { RootState } from 'src/state/store';
import {
  CANNOT_CHANGE_DATA,
  findStopName,
  firstUserErrorMessage,
} from 'src/utils';
import { NotificationType, showNotification } from '../notification';
import { getAllStopStatuses } from './getAllStopStatues';
import { getPrimaryStopStatuses } from './getPrimaryStopStatuses';
import { getRoutes } from './getRoutes';

type MarkCustomerPickupStopsCompleteArgs = {
  stopIds: string[];
  warehouseId: string;
  nightEnd: string;
};

type UserError = NonNullable<
  GMarkCustomerPickupStopsCompleteMutation['markCustomerPickupStopsComplete']
>['userErrors'][number];

const STALE_STOPS_ERROR_CODE = 'stale_customer_pickup_stops';

// The server refuses the whole call when a stop holds an order that was
// canceled or moved to another night, and names the stops by id, which a GM
// cannot map to a row. Name them by business so the GM can deselect them; the
// tab keeps them selectable because they are still open.
const refusalMessage = (
  userErrors: ReadonlyArray<UserError>,
  state: RootState,
): string => {
  const refusal = userErrors[0];
  const fallback = firstUserErrorMessage(userErrors);
  if (
    refusal?.__typename !== 'NonCustomerPickupStops' ||
    refusal.errorCode !== STALE_STOPS_ERROR_CODE
  ) {
    return fallback;
  }
  const stops = (state.delivery.routes ?? []).flatMap(
    (route) => route.stops ?? [],
  );
  const names = refusal.stopIds.flatMap((id) => {
    const stop = stops.find((candidate) => candidate.id === id);
    return stop ? [findStopName(stop, state.delivery.selectedWarehouseId)] : [];
  });
  if (names.length === 0) return fallback;
  const one = names.length === 1;
  return `Nothing was marked picked up. ${names.join(', ')} ${
    one ? 'has' : 'have'
  } an order that was canceled or moved to another night: deselect ${
    one ? 'it' : 'them'
  } and mark the rest.`;
};

export const markCustomerPickupStopsComplete = createAsyncThunk(
  'delivery/markCustomerPickupStopsComplete',
  async (
    { stopIds, warehouseId, nightEnd }: MarkCustomerPickupStopsCompleteArgs,
    thunkAPI,
  ): Promise<boolean> => {
    // An empty selection is rejected server-side as a top-level GraphQL error,
    // not a userErrors entry, so it would surface as a bare generic toast.
    // Callers guard too; this is the backstop.
    if (stopIds.length === 0) return false;

    // A past night is allowed: the tab is where a night is closed, and the
    // server refuses a stop holding an order rescheduled off the night or
    // canceled. This thunk is the chokepoint both the per-row and bulk flows
    // funnel through, so it still refuses before anything is written while the
    // warehouse is unloaded.
    const state = thunkAPI.getState() as RootState;
    if (!selectedWarehouseSelector(state)) {
      thunkAPI.dispatch(
        showNotification({
          type: NotificationType.Error,
          message: CANNOT_CHANGE_DATA,
        }),
      );
      return false;
    }

    const { data, error } = await client
      .mutation<GMarkCustomerPickupStopsCompleteMutation>(
        MARK_CUSTOMER_PICKUP_STOPS_COMPLETE,
        { input: { stopIds } },
      )
      .toPromise();

    const payload = data?.markCustomerPickupStopsComplete;
    const userErrors = payload?.userErrors ?? [];

    if (error || !payload || userErrors.length > 0) {
      // This action bills the customer and cannot be undone, so a systematic
      // failure must be visible in prod, not only in the operator's toast. A
      // GraphQL 200 carrying userErrors is not a failed request, so
      // forwardErrorsToLogs never sees it.
      logWarning('customer_pickup.mark_complete_failed', {
        stopCount: stopIds.length,
        userError: userErrors[0]?.__typename,
        transportError: Boolean(error),
      });
      thunkAPI.dispatch(
        showNotification({
          type: NotificationType.Error,
          message: userErrors.length
            ? refusalMessage(userErrors, thunkAPI.getState() as RootState)
            : undefined,
        }),
      );

      // A typed userError means the server disagreed with what this client
      // thought was completable, so the local routes are stale: the whole call
      // is rejected before anything is written, and a selection built on the
      // stale view would keep failing. Re-syncing lets the tab drop the ids
      // that are no longer eligible. A transport error teaches us nothing about
      // server state, so it gets no refetch.
      if (userErrors.length > 0) {
        thunkAPI.dispatch(getRoutes({ warehouseId, nightEnd }));
      }
      return false;
    }

    thunkAPI.dispatch(
      showNotification({
        type: NotificationType.Success,
        message:
          stopIds.length === 1
            ? 'Order marked as picked up.'
            : `${stopIds.length} orders marked as picked up.`,
      }),
    );

    // Three separate projections: state.routes carries completedAt (the Picked
    // Up pill), and the row status icons read whichever of the all/primary
    // stop-status caches the flag selects. refreshRouteData refetches all
    // three. The payload's `routes` is nullable, so refetching rather than
    // merging also covers a successful call that returns no routes.
    thunkAPI.dispatch(getRoutes({ warehouseId, nightEnd }));
    thunkAPI.dispatch(getAllStopStatuses({ warehouseId, nightEnd }));
    thunkAPI.dispatch(getPrimaryStopStatuses({ warehouseId, nightEnd }));

    // Last, so a failure here cannot skip a refresh. Read from the snapshot
    // taken before the request: it is the night the GM confirmed. Counts how
    // often past nights are closed from the tab; who closed what is the
    // server's audit.
    if (isSelectedNightEndBeforeWarehouseNightEnd(state)) {
      const warehouse = selectedWarehouseSelector(state);
      analytics.track('Customer Pickup Past Night Marked Picked Up', {
        warehouseId,
        warehouseName: warehouse?.name,
        nightEnd,
        stopCount: stopIds.length,
        daysPast: selectedNightDaysPastSelector(state),
      });
    }

    return true;
  },
);
